"use client";

import { api } from "@v1/backend/convex/_generated/api";
import { useAction } from "convex/react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactElement, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { applyClipPlayback } from "../clip-playback-access";
import { useClipPlaybackRecovery } from "../player/use-clip-playback-recovery";
import { useClipWatchViewTracking } from "../use-clip-watch-view-tracking";
import { ClipVideoPlayer } from "./clip-video-player";

/**
 * Native S3-hosted player for the public share page. The playback URL is a
 * direct presigned S3 GET minted by an action that re-validates id +
 * visibility; on expiry/error it refreshes and resumes currentTime (see
 * useClipPlaybackRecovery for the rules that keep that loop from spinning).
 * View counting: >10s cumulative play time (see useClipWatchViewTracking).
 *
 * A `videoRef` may be supplied by the watch page so sibling UI (reaction bar,
 * comment composer) can read the current playback time for timestamps.
 */
export function ClipSharePlayer({
  clipId,
  posterUrl,
  videoRef,
  autoPlay = true,
  seekingDisabled = false,
}: {
  readonly clipId: string;
  readonly posterUrl: string | null;
  readonly videoRef?: RefObject<HTMLVideoElement | null>;
  readonly autoPlay?: boolean;
  /** Viewer seeking lock — no progress bar, no skips (linear-only files). */
  readonly seekingDisabled?: boolean;
}): ReactElement {
  const t = useTranslations("clips.clipSharePlayer");
  const getUrl = useAction(api.clips.playback.getPublicClipPlaybackUrlById);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const internalRef = useRef<HTMLVideoElement>(null);
  const activeRef = videoRef ?? internalRef;
  const loadGenRef = useRef(0);
  const loadRef = useRef<(() => Promise<void>) | null>(null);

  useClipWatchViewTracking(activeRef, clipId, url !== null && !failed);

  const { videoAutoPlay, handleError, handleLoadedData, spendRetry, reset } =
    useClipPlaybackRecovery({
      videoRef: activeRef,
      autoPlay,
      requestNewUrl: () => void loadRef.current?.(),
      onExhausted: () => setFailed(true),
    });

  const load = useCallback(async (): Promise<void> => {
    const gen = ++loadGenRef.current;
    try {
      const result = await getUrl({ clip_id: clipId });
      if (gen !== loadGenRef.current) return;
      const nextUrl = applyClipPlayback(result);
      if (nextUrl) {
        setUrl(nextUrl);
        setFailed(false);
      } else {
        // Null is the gate's deterministic "no access" — retrying can't help.
        setFailed(true);
      }
    } catch {
      if (gen !== loadGenRef.current) return;
      // Transient action failure (network blip mid-recovery) — spend the same
      // retry budget as a media error instead of failing outright.
      if (!spendRetry()) {
        setFailed(true);
        return;
      }
      void load();
    }
  }, [getUrl, clipId, spendRetry]);
  loadRef.current = load;

  useEffect(() => {
    reset();
    void load();
    return () => {
      loadGenRef.current += 1;
    };
  }, [load, reset]);

  if (failed) {
    return (
      <div className="flex aspect-video w-full items-center justify-center bg-neutral-900 text-sm text-white/60 lg:rounded-2xl">
        {t("videoKonnteNichtGeladen")}
      </div>
    );
  }
  if (!url) {
    return (
      <div className="flex aspect-video w-full items-center justify-center bg-neutral-900 text-white/50 lg:rounded-2xl">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  return (
    <ClipVideoPlayer
      ref={activeRef}
      src={url}
      poster={posterUrl}
      autoPlay={videoAutoPlay}
      seekingDisabled={seekingDisabled}
      onError={handleError}
      onLoadedData={handleLoadedData}
      className="rounded-none shadow-none ring-0 lg:rounded-2xl lg:shadow-lg lg:ring-1 lg:ring-black/[0.06]"
    />
  );
}
